import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@tanium/react-confirm-modal';
import { useToastNotificationsControl } from '@tanium/react-toast-notifications-context';
import { AsyncStatus } from '../../hooks/useAsync';
import { useTenantDomains } from '../../hooks/useTenantDomains';

interface ModalProps {
  isOpen: boolean;
  domainId: string;
  onRequestClose: () => void;
}

export const DeleteDomainModal: React.FC<ModalProps> = ({ isOpen, domainId, onRequestClose }) => {
  const { t } = useTranslation();
  const { deleteDomain } = useTenantDomains();
  const { showNotification } = useToastNotificationsControl();

  useEffect(() => {
    if (deleteDomain.status === AsyncStatus.Success) {
      onRequestClose();
      deleteDomain.reset();
      showNotification({
        type: 'success',
        contents: t('Successfully deleted domain'),
      });
    } else if (deleteDomain.status === AsyncStatus.Failure) {
      onRequestClose();
      deleteDomain.reset();
      showNotification({
        type: 'error',
        contents: t('Failed to delete Domain'),
      });
    }
  }, [onRequestClose, deleteDomain, showNotification, t]);

  return (
    <ConfirmModal
      isOpen={isOpen}
      header={t('Confirm Delete Domain')}
      onConfirm={async () => {
        await deleteDomain.execute(domainId);
      }}
      isConfirmDisabled={deleteDomain.status === AsyncStatus.Pending}
      onCancel={() => {
        onRequestClose();
        deleteDomain.reset();
      }}
    >
      <>{t('Are you sure you wish to delete this domain?')}</>
    </ConfirmModal>
  );
};
